/**
 * Idle screen lock (spec 09 §4, dent-cloud-service/.superpower/2fa-totp/).
 *
 * Locks the screen after a clinic-configured idle period without touching the
 * session: tokens, Vuex state and half-typed forms all survive, and unlocking
 * puts the user back exactly where they were.
 *
 * Only real DOM interaction resets the timer. Nothing driven by a timer of the
 * application's own — polling, silent token refresh, autosave, the queue
 * screens' colour ticks — may count as activity, or the screen never locks on
 * an empty room and the feature is inert rather than visibly broken. That
 * failure is silent, which is why the event list below is a closed set.
 *
 * The locked flag lives in localStorage so a reload does not clear it, and the
 * `storage` event carries lock and unlock across tabs of the same browser.
 */

const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "click",
  "touchstart",
  "scroll",
];

// One timestamp write per 5s at most. mousemove alone fires hundreds of times
// a second; the timer only needs to know "was there anything in this window".
const THROTTLE_MS = 5000;

// How often we compare now against the last activity. One second is well
// inside the smallest configurable timeout (5 minutes) and costs nothing.
const TICK_MS = 1000;

export const LOCK_STORAGE_KEY = "dc_lock_state";
const LOCKED = "locked";

// Bumped when the active user changes on this machine. Every other tab
// reloads, because a switch is an identity change, not an unlock.
export const SWITCH_EPOCH_KEY = "dc_switch_epoch";

/**
 * @param {object} options
 * @param {number|null} options.timeoutMinutes null switches the lock off
 * @param {Function} [options.onLock] called when the screen should lock
 * @param {Function} [options.onUnlock] called when it should unlock
 * @param {Function} [options.isSuspended] return true to pause locking
 *        entirely — used for unattended display screens, where a lock screen
 *        would be shown to patients rather than to staff
 */
export function createIdleLock({
  timeoutMinutes,
  onLock,
  onUnlock,
  isSuspended,
}) {
  let lastActivity = Date.now();
  let timer = null;
  let locked = false;

  const isLocked = () => locked;

  const lock = () => {
    if (locked) return;
    locked = true;
    try {
      window.localStorage.setItem(LOCK_STORAGE_KEY, LOCKED);
    } catch (e) {
      // Private mode or a full quota. The in-memory flag still locks this tab;
      // only the survives-a-reload and cross-tab guarantees are lost.
    }
    if (onLock) onLock();
  };

  const unlock = () => {
    if (!locked) return;
    locked = false;
    try {
      window.localStorage.removeItem(LOCK_STORAGE_KEY);
    } catch (e) {
      // See lock().
    }
    lastActivity = Date.now();
    if (onUnlock) onUnlock();
  };

  const onActivity = () => {
    if (locked) return;
    const now = Date.now();
    if (now - lastActivity < THROTTLE_MS) return;
    lastActivity = now;
  };

  const tick = () => {
    if (locked || !timeoutMinutes) return;
    // Re-checked every tick rather than at start(), so navigating onto and off
    // a display screen takes effect immediately.
    if (isSuspended && isSuspended()) {
      lastActivity = Date.now();
      return;
    }
    if (Date.now() - lastActivity >= timeoutMinutes * 60 * 1000) lock();
  };

  // Fires only in OTHER tabs, which is exactly what makes it the cross-tab
  // channel: the tab that wrote the key does not hear its own event.
  const onStorage = event => {
    // A switch changed who this browser is. Reloading is the only correct
    // response: this tab still holds the PREVIOUS user's Vuex state — their
    // name, their branch, their patients on screen — while every request from
    // now on carries the new user's token. Handled before the lock key so a
    // switch never degrades into a mere unlock.
    if (event.key === SWITCH_EPOCH_KEY && event.newValue) {
      window.location.reload();
      return;
    }
    if (event.key !== LOCK_STORAGE_KEY) return;
    if (event.newValue === LOCKED) lock();
    else unlock();
  };

  const start = () => {
    stop();
    if (!timeoutMinutes) return;

    // A reload while locked must land locked, not on an open app for the
    // second or two before the first tick.
    let stored = null;
    try {
      stored = window.localStorage.getItem(LOCK_STORAGE_KEY);
    } catch (e) {
      stored = null;
    }
    if (stored === LOCKED) lock();

    lastActivity = Date.now();
    ACTIVITY_EVENTS.forEach(name =>
      window.addEventListener(name, onActivity, { passive: true })
    );
    window.addEventListener("storage", onStorage);
    timer = window.setInterval(tick, TICK_MS);
  };

  const stop = () => {
    ACTIVITY_EVENTS.forEach(name =>
      window.removeEventListener(name, onActivity)
    );
    window.removeEventListener("storage", onStorage);
    if (timer) window.clearInterval(timer);
    timer = null;
  };

  return { start, stop, lock, unlock, isLocked };
}

/**
 * Clear the persisted lock flag. Called on logout, so the next person to log
 * in on this machine does not land on a lock screen belonging to nobody.
 */
export function clearLockState() {
  try {
    window.localStorage.removeItem(LOCK_STORAGE_KEY);
  } catch (e) {
    // Nothing to do — the flag is best-effort persistence, not state of record.
  }
}

/**
 * Announce that the active user on this machine has changed.
 *
 * MUST be called BEFORE clearLockState(). Both fire `storage` in sibling tabs,
 * and clearing the lock key alone reads as "unlock" — siblings would release
 * into the PREVIOUS user's rendered state and could paint a frame of it before
 * the epoch event arrives in the next task. Bumping the epoch first means they
 * reload while still locked.
 */
export function announceUserSwitch() {
  try {
    window.localStorage.setItem(SWITCH_EPOCH_KEY, String(Date.now()));
  } catch (e) {
    // Private mode: sibling tabs keep the old identity until their own next
    // request fails. Nothing better is available without storage.
  }
}
